"use client";

import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Pencil } from "lucide-react";
import type { BaseEntity, SimpleFieldConfig } from "../../UniversalCRMTable/types";
import { toast } from "sonner";
import {
  renderFieldInputWithCustomHandling as renderFieldInputWithCustomHandlingHelper,
  renderFieldDisplay as renderFieldDisplayHelper,
} from "../utils/field-rendering-helpers";
import {
  isInlineToggleField,
  getToggleOptions,
  getCurrentToggleValue,
  getCopyableValue,
} from "./field-row/toggle-helpers";
import { FieldRowToggle } from "./field-row/FieldRowToggle";
import { FieldRowEditing } from "./field-row/FieldRowEditing";
import { FieldRowDisplay } from "./field-row/FieldRowDisplay";
import { FieldHelpPopover } from "@/components/shared/FieldHelp/FieldHelpPopover";
import {
  areFieldRowPropsEqual,
  type FieldRowProps,
} from "./field-row/areFieldRowPropsEqual";

function FieldRowComponent<T extends BaseEntity>({
  fieldId,
  fieldConfig,
  value,
  isFieldEditing,
  canEdit,
  isFullscreen,
  item,
  config,
  onFieldEdit,
  onSaveField,
  onCancelEdit,
  onFieldValueChange,
}: FieldRowProps<T>) {
  const handleToggleSave = useCallback(
    async (newRawValue: string) => {
      const parsedValue =
        fieldConfig.type === "boolean" ? newRawValue === "true" : newRawValue;
      await onSaveField(fieldId, parsedValue);
    },
    [fieldConfig.type, fieldId, onSaveField]
  );

  const renderFieldInputWithCustomHandling = useCallback(
    (fId: string, fc: SimpleFieldConfig, v: unknown): React.ReactNode => {
      return renderFieldInputWithCustomHandlingHelper(
        fId,
        fc,
        v,
        item,
        onFieldValueChange,
        onSaveField,
        config,
        (e: React.KeyboardEvent) => {
          // Save on Enter key (but not for textarea or multi-line fields)
          if (e.key === "Enter" && fc.type !== "textarea" && !e.shiftKey) {
            e.preventDefault();
            void onSaveField(fId);
          }
        }
      );
    },
    [item, onFieldValueChange, onSaveField, config]
  );

  const renderFieldDisplay = useCallback(
    (fc: SimpleFieldConfig, v: unknown, fId?: string) => {
      return renderFieldDisplayHelper(fc, v, fId, item, isFieldEditing ? fieldId : null, config?.entity);
    },
    [item, isFieldEditing, fieldId, config?.entity]
  );

  const handleCopyValue = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(getCopyableValue(value));
      toast.success(`${fieldConfig.label} copied`);
    } catch {
      toast.error("Unable to copy value");
    }
  }, [fieldConfig.label, value]);

  const showToggle = canEdit && fieldConfig.editable !== false && isInlineToggleField(fieldConfig);
  const toggleOptions = showToggle ? getToggleOptions(fieldConfig) : [];
  const currentToggleValue = showToggle ? getCurrentToggleValue(value, fieldConfig) : "";

  return (
    <div className="space-y-1">
      <div className="flex items-start justify-between gap-2">
        <label className="text-xs md:text-sm font-medium text-muted-foreground flex flex-1 items-center gap-1.5 break-words">
          {fieldConfig.label}
          <FieldHelpPopover entity={config.entity} fieldId={fieldId} side="top" align="start" />
        </label>
        {canEdit && !isFieldEditing && fieldConfig.editable !== false && !showToggle && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onFieldEdit(fieldId)}
            className="h-10 w-10 sm:h-8 sm:w-8 p-0 hover:bg-card flex-shrink-0"
            title="Edit"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>

      {showToggle && !isFieldEditing ? (
        <FieldRowToggle
          options={toggleOptions}
          currentValue={currentToggleValue}
          onSelect={handleToggleSave}
        />
      ) : isFieldEditing ? (
        <FieldRowEditing
          fieldId={fieldId}
          fieldConfig={fieldConfig}
          value={value}
          isFullscreen={isFullscreen}
          renderInput={renderFieldInputWithCustomHandling}
          onSave={onSaveField}
          onCancel={onCancelEdit}
        />
      ) : (
        <FieldRowDisplay
          fieldId={fieldId}
          fieldConfig={fieldConfig}
          value={value}
          canEdit={canEdit}
          isFieldEditing={isFieldEditing}
          onEdit={onFieldEdit}
          onCopy={handleCopyValue}
          renderDisplay={renderFieldDisplay}
        />
      )}
    </div>
  );
}

export const FieldRow = React.memo(FieldRowComponent, areFieldRowPropsEqual) as typeof FieldRowComponent;
